<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useSettingsStore } from '../stores/settingsStore'
import SocialShare from '../components/SocialShare.vue'

const settingsStore = useSettingsStore()

const route = useRoute()
const article = ref(null)
const loading = ref(true)
const relatedArticles = ref([])
const recommendedProducts = ref([])
const relatedProducts = ref([])
const recommendedArticles = ref([])

// Utility to transform oembed to iframe
const transformOembed = (html) => {
  if (!html) return html;
  
  const buildYouTubeEmbed = (videoId) => {
    const embedUrl = `https://www.youtube.com/embed/${videoId}`;
    return `<div class="yt-embed-wrapper"><div class="yt-embed-inner"><iframe src="${embedUrl}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe></div></div>`;
  };

  const extractYouTubeId = (url) => {
    if (url.startsWith('www.')) url = 'https://' + url;
    const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|shorts\/|watch\?v=|watch\?.+&v=))([\w-]{11})/i);
    return ytMatch ? ytMatch[1] : null;
  };

  // 1. Transform <figure class="media"><oembed>...</oembed></figure> (CKEditor full structure)
  let result = html.replace(/<figure\s+class="media">\s*<oembed\s+url="([^"]+)">\s*<\/oembed>\s*<\/figure>/gi, (match, url) => {
    const videoId = extractYouTubeId(url);
    if (videoId) return buildYouTubeEmbed(videoId);
    return `<div class="yt-embed-wrapper"><div class="yt-embed-inner"><iframe src="${url}" frameborder="0" allowfullscreen></iframe></div></div>`;
  });

  // 2. Transform standalone <oembed> tags (without figure wrapper)
  result = result.replace(/<oembed\s+url="([^"]+)"><\/oembed>/gi, (match, url) => {
    const videoId = extractYouTubeId(url);
    if (videoId) return buildYouTubeEmbed(videoId);
    return `<div class="yt-embed-wrapper"><div class="yt-embed-inner"><iframe src="${url}" frameborder="0" allowfullscreen></iframe></div></div>`;
  });

  // 3. Transform <figure class="media"> wrapping an iframe
  result = result.replace(/<figure\s+class="media">\s*(<iframe[^>]*?>[\s\S]*?<\/iframe>)\s*<\/figure>/gi, (match, iframeTag) => {
    const ytMatch = iframeTag.match(/src="https?:\/\/(?:www\.)?youtube(?:-nocookie)?\.com\/embed\/([\w-]{11})([^"]*)"/i);
    if (ytMatch) return buildYouTubeEmbed(ytMatch[1]);
    return `<div class="yt-embed-wrapper"><div class="yt-embed-inner">${iframeTag}</div></div>`;
  });

  // 4. Fix any remaining standalone youtube iframes not inside figure.media
  result = result.replace(/<iframe([^>]*?)src="https?:\/\/(?:www\.)?youtube(?:-nocookie)?\.com\/embed\/([\w-]{11})([^"]*)"([^>]*?)>(\s*<\/iframe>)?/gi, (match, before, videoId, params, after) => {
    return buildYouTubeEmbed(videoId);
  });

  return result;
};

const loadArticle = async () => {
  loading.value = true
  try {
    const res = await fetch(`/api/articles/${route.params.slug}`)
    const data = await res.json()
    if (data.success) {
      article.value = data.data
      if (article.value.content) {
        article.value.content = transformOembed(article.value.content)
      }
      addSchema()
      updateMetaTags()
      // Load related
      loadRelated()
    }
  } catch (e) { console.error(e) } finally { loading.value = false }
}

const updateMetaTags = () => {
    const titleText = article.value.seo_title || article.value.title || (settingsStore.storeName ? `บทความ | ${settingsStore.storeName}` : 'บทความ')
    document.title = titleText

    const setMeta = (name, content, isProp = false) => {
        if (!content) return
        const attr = isProp ? 'property' : 'name'
        let el = document.querySelector(`meta[${attr}="${name}"]`)
        if (!el) {
            el = document.createElement('meta')
            el.setAttribute(attr, name)
            document.head.appendChild(el)
            el.classList.add('dynamic-seo-tag')
        }
        el.setAttribute('content', content)
    }

    const desc = article.value.seo_description || article.value.excerpt || ''
    const keywords = article.value.seo_keywords || article.value.tags || ''
    const url = window.location.href
    const image = article.value.cover_image || ''

    // Standard SEO
    setMeta('description', desc)
    setMeta('keywords', keywords)

    // OpenGraph (Facebook, Line)
    setMeta('og:title', titleText, true)
    setMeta('og:description', desc, true)
    setMeta('og:image', image, true)
    setMeta('og:url', url, true)
    setMeta('og:type', 'article', true)

    // Twitter Card
    setMeta('twitter:card', 'summary_large_image')
    setMeta('twitter:title', titleText)
    setMeta('twitter:description', desc)
    setMeta('twitter:image', image)

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]')
    if (!canonical) {
        canonical = document.createElement('link')
        canonical.setAttribute('rel', 'canonical')
        canonical.classList.add('dynamic-seo-tag')
        document.head.appendChild(canonical)
    }
    canonical.setAttribute('href', url)
}

const loadRelated = async () => {
  try {
    // 1. Related articles (same category)
    const res = await fetch(`/api/articles/published?limit=6&category=${encodeURIComponent(article.value.category)}`)
    const data = await res.json()
    if (data.success) {
      relatedArticles.value = data.data.filter(a => a.id !== article.value.id).slice(0, 3)
    }

    // 2. Recommended articles (different category or all, excluding current and already-related)
    const recRes = await fetch(`/api/articles/published?limit=8`)
    const recData = await recRes.json()
    if (recData.success) {
      const excludeIds = new Set([article.value.id, ...relatedArticles.value.map(a => a.id)])
      recommendedArticles.value = recData.data.filter(a => !excludeIds.has(a.id)).slice(0, 4)
    }

    // 3. Recommended products (popular / featured)
    const prodRes = await fetch(`/api/products`)
    const prodData = await prodRes.json()
    if (prodData.success) {
      const allProducts = prodData.data.filter(p => p.is_active && !p.is_out_of_stock)
      // Exclude the article's linked product if any
      const excludeProductId = article.value.product_id || null
      const filtered = allProducts.filter(p => p.id !== excludeProductId)
      recommendedProducts.value = filtered.slice(0, 4)

      // 4. Related products (same category as article)
      if (article.value.category) {
        const catProducts = allProducts.filter(p =>
          p.category && p.category.includes(article.value.category.split(',')[0]?.trim()) && p.id !== excludeProductId
        )
        relatedProducts.value = catProducts.slice(0, 4)
        // If not enough category matches, fill with others
        if (relatedProducts.value.length < 2) {
          const existingIds = new Set(relatedProducts.value.map(p => p.id))
          const fillers = filtered.filter(p => !existingIds.has(p.id)).slice(0, 4 - relatedProducts.value.length)
          relatedProducts.value = [...relatedProducts.value, ...fillers]
        }
      }
    }
  } catch (e) { console.error('loadRelated error:', e) }
}

const formatPrice = (price) => {
  if (!price || price <= 0) return 'สอบถามราคา'
  return Number(price).toLocaleString() + ' บาท'
}

const addSchema = () => {
  // Remove existing
  const existing = document.getElementById('json-ld-article')
  if (existing) document.head.removeChild(existing)

  const baseUrl = settingsStore.storeUrl || window.location.origin
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.value.title,
    "description": article.value.seo_description || article.value.excerpt,
    "image": article.value.cover_image || '',
    "author": { "@type": "Person", "name": article.value.author || "Admin" },
    "publisher": {
      "@type": "Organization",
      "name": settingsStore.storeName || '',
      "url": baseUrl
    },
    "datePublished": article.value.created_at,
    "dateModified": article.value.updated_at,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": window.location.href
    }
  }
  const s = document.createElement('script')
  s.type = 'application/ld+json'
  s.text = JSON.stringify(schema)
  s.id = 'json-ld-article'
  document.head.appendChild(s)

  // Breadcrumb
  const bc = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "หน้าแรก", "item": `${baseUrl}/` },
      { "@type": "ListItem", "position": 2, "name": "บทความ", "item": `${baseUrl}/blog` },
      { "@type": "ListItem", "position": 3, "name": article.value.title, "item": window.location.href }
    ]
  }
  const bcS = document.createElement('script')
  bcS.type = 'application/ld+json'
  bcS.text = JSON.stringify(bc)
  bcS.id = 'json-ld-article-bc'
  document.head.appendChild(bcS)

  // FAQ Schema
  if (article.value.faq && article.value.faq.length > 0) {
    let parsedFaq = []
    if (typeof article.value.faq === 'string') {
      try { parsedFaq = JSON.parse(article.value.faq) } catch (e) { console.error('Error parsing FAQ schema:', e) }
    } else {
      parsedFaq = article.value.faq
    }

    if (parsedFaq.length > 0) {
      const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": parsedFaq.map(item => ({
          "@type": "Question",
          "name": item.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": item.answer
          }
        }))
      }
      const faqS = document.createElement('script')
      faqS.type = 'application/ld+json'
      faqS.text = JSON.stringify(faqSchema)
      faqS.id = 'json-ld-article-faq'
      document.head.appendChild(faqS)
    }
  }
}

const formatDate = (d) => d ? new Date(d).toLocaleDateString('th-TH', { day: 'numeric', month: 'long', year: 'numeric' }) : ''
const openFaqIndex = ref(null)
const toggleFaq = (index) => {
  openFaqIndex.value = openFaqIndex.value === index ? null : index
}

// Lightbox state and functions
const isLightboxOpen = ref(false)
const currentImageIndex = ref(0)

const openLightbox = (index) => {
    currentImageIndex.value = index
    isLightboxOpen.value = true
    document.body.style.overflow = 'hidden'
}

const closeLightbox = () => {
    isLightboxOpen.value = false
    document.body.style.overflow = ''
}

const nextImage = () => {
    if (article.value && article.value.gallery_images) {
        currentImageIndex.value = (currentImageIndex.value + 1) % article.value.gallery_images.length
    }
}

const prevImage = () => {
    if (article.value && article.value.gallery_images) {
        currentImageIndex.value = (currentImageIndex.value - 1 + article.value.gallery_images.length) % article.value.gallery_images.length
    }
}

const handleKeydown = (e) => {
    if (!isLightboxOpen.value) return
    if (e.key === 'Escape') closeLightbox()
    if (e.key === 'ArrowRight') nextImage()
    if (e.key === 'ArrowLeft') prevImage()
}

const scrollPercent = ref(0)
const activeRecommendTab = ref('products')
const productSliderRef = ref(null)
const articleSliderRef = ref(null)

const updateScrollProgress = () => {
  const totalHeight = document.documentElement.scrollHeight - window.innerHeight
  if (totalHeight <= 0) {
    scrollPercent.value = 0
    return
  }
  scrollPercent.value = Math.min((window.scrollY / totalHeight) * 100, 100)
}

const scrollSlider = (direction) => {
  const activeSlider = activeRecommendTab.value === 'products' ? productSliderRef.value : articleSliderRef.value
  if (!activeSlider) return
  const scrollAmount = 300
  if (direction === 'left') {
    activeSlider.scrollLeft -= scrollAmount
  } else {
    activeSlider.scrollLeft += scrollAmount
  }
}

const getReadingTime = (article) => {
  if (!article) return 2
  const textLength = (article.title || '').length + (article.excerpt || '').length + (article.content || '').length;
  const time = Math.ceil(textLength / 250); 
  return time < 2 ? 2 : time;
}

const parsedFaqList = computed(() => {
  if (!article.value?.faq) return []
  if (typeof article.value.faq === 'string') {
      try { return JSON.parse(article.value.faq) } catch (e) { return [] }
  }
  return article.value.faq || []
})

onMounted(() => {
    loadArticle()
    window.addEventListener('keydown', handleKeydown)
    window.addEventListener('scroll', updateScrollProgress)
})

onUnmounted(() => {
  ['json-ld-article', 'json-ld-article-bc', 'json-ld-article-faq'].forEach(id => {
    const s = document.getElementById(id)
    if (s) document.head.removeChild(s)
  })
  
  window.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('scroll', updateScrollProgress)
  document.body.style.overflow = ''
})
</script>

<template>
  <div class="bg-gray-50/30 dark:bg-[#0a0f16] min-h-screen pt-24 md:pt-32 pb-20 transition-colors relative overflow-hidden">
    <!-- Scroll progress bar -->
    <div class="fixed top-0 left-0 w-full h-[3px] bg-gray-100/10 dark:bg-gray-800/10 z-[9999]">
      <div class="h-full bg-gradient-to-r from-emerald-500 to-teal-400 transition-all duration-75" :style="{ width: scrollPercent + '%' }"></div>
    </div>

    <!-- Ambient Decor (Animated Background) -->
    <div class="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCI+CgkJPGcgc3Ryb2tlPSIjMTA4YjY1IiBzdHJva2Utd2lkdGg9IjEiIG9wYWNpdHk9IjAuMDUiPgoJCQk8cGF0aCBkPSJNMCAwaDQwdjQwaC00MHoiIGZpbGw9Im5vbmUiLz4KCQk8L2c+Cjwvc3ZnPg==')] [mask-image:linear-gradient(to_bottom,white_10%,transparent_80%)] dark:[mask-image:linear-gradient(to_bottom,rgba(255,255,255,0.05)_10%,transparent_80%)] z-0"></div>
    <div class="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
      <div class="absolute -top-[10%] right-[0%] w-[60vw] max-w-[600px] h-[60vw] max-h-[600px] bg-emerald-500/10 dark:bg-emerald-500/5 blur-[120px] rounded-full animate-[spin_20s_linear_infinite] origin-center mix-blend-multiply dark:mix-blend-lighten"></div>
      <div class="absolute top-[20%] -left-[10%] w-[50vw] max-w-[500px] h-[50vw] max-h-[500px] bg-teal-500/10 dark:bg-teal-500/5 blur-[100px] rounded-full animate-[pulse_10s_ease-in-out_infinite] mix-blend-multiply dark:mix-blend-lighten"></div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex justify-center items-center min-h-[50vh]">
      <div class="w-12 h-12 border-4 border-emerald-200 border-t-emerald-505 rounded-full animate-spin"></div>
    </div>

    <div v-else-if="article" class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      
      <!-- Breadcrumb -->
      <nav class="text-sm text-gray-400 dark:text-gray-500 mb-8 font-medium overflow-hidden" aria-label="Breadcrumb">
        <ol class="flex items-center min-w-0 gap-2">
          <li class="flex-shrink-0"><router-link to="/" class="hover:text-emerald-500 transition-colors">หน้าแรก</router-link></li>
          <li class="flex-shrink-0"><span class="text-gray-300 dark:text-gray-700">/</span></li>
          <li class="flex-shrink-0"><router-link to="/blog" class="hover:text-emerald-500 transition-colors">บทความ</router-link></li>
          <li class="flex-shrink-0"><span class="text-gray-300 dark:text-gray-700">/</span></li>
          <li class="min-w-0"><span class="text-gray-900 dark:text-white block truncate font-bold">{{ article.title }}</span></li>
        </ol>
      </nav>

      <!-- Editorial Split Hero -->
      <header class="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center mb-16">
        <!-- Left Column: Content metadata -->
        <div class="lg:col-span-7 flex flex-col justify-center">
          <div class="flex items-center flex-wrap gap-3 mb-6">
            <span class="px-3.5 py-1.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-bold rounded-xl border border-emerald-100 dark:border-emerald-500/10">{{ article.category }}</span>
            <span class="text-xs font-semibold text-gray-450 dark:text-gray-500 flex items-center gap-1.5">
              <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
              {{ formatDate(article.created_at) }}
            </span>
            <span class="text-xs font-semibold text-gray-450 dark:text-gray-500 flex items-center gap-1.5">
              <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
              {{ article.view_count || 0 }} วิว
            </span>
            <span class="text-xs font-semibold text-gray-450 dark:text-gray-500 flex items-center gap-1.5">
              <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/></svg>
              อ่าน {{ getReadingTime(article) }} นาที
            </span>
          </div>
          
          <h1 class="text-3xl md:text-4xl lg:text-[2.75rem] font-black text-gray-900 dark:text-white tracking-tight leading-[1.25] mb-6">{{ article.title }}</h1>
          <p v-if="article.excerpt" class="text-base md:text-lg text-gray-500 dark:text-gray-450 font-light leading-relaxed mb-8 border-l-2 border-emerald-500 pl-4">{{ article.excerpt }}</p>
          
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold">{{ (article.author || 'A')[0] }}</div>
            <div>
              <p class="text-sm font-bold text-gray-900 dark:text-white">{{ article.author || 'Admin' }}</p>
              <p class="text-xs text-gray-450">{{ settingsStore.storeName ? `${settingsStore.storeName} Team` : 'Editorial Team' }}</p>
            </div>
          </div>
        </div>

        <!-- Right Column: Cover Image -->
        <div v-if="article.cover_image" class="lg:col-span-5 relative group">
          <div class="absolute -inset-2 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-[2.2rem] blur opacity-15 group-hover:opacity-30 transition duration-500"></div>
          <div class="relative aspect-[16/10] rounded-[2rem] overflow-hidden shadow-2xl border border-gray-100 dark:border-gray-800">
            <img :src="article.cover_image" :alt="article.title" class="w-full h-full object-cover transform group-hover:scale-102 transition-transform duration-[2s]">
          </div>
        </div>
      </header>

      <!-- Two-Column content layout -->
      <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start">
        
        <!-- Main body: 8 cols -->
        <div class="lg:col-span-8">
          <article class="prose prose-lg prose-gray dark:prose-invert max-w-none mb-16 font-light leading-relaxed ck-content" v-html="article.content"></article>

          <!-- FAQ Section -->
          <div v-if="parsedFaqList.length > 0" class="mb-16 pt-8 border-t border-gray-100 dark:border-gray-800">
            <h3 class="text-2xl font-black text-gray-900 dark:text-white mb-8 flex items-center gap-3">
              <div class="p-2.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-xl">
                 <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
              </div>
              คำถามที่พบบ่อย (FAQ)
            </h3>
            <div class="space-y-4">
              <div v-for="(faq, index) in parsedFaqList" :key="index" class="bg-white/50 dark:bg-gray-900/20 backdrop-blur-sm rounded-2xl overflow-hidden border border-gray-200/60 dark:border-white/5 shadow-sm transition-all duration-305">
                <button @click="toggleFaq(index)" class="w-full flex items-center justify-between p-5 md:p-6 text-left focus:outline-none group">
                  <span class="font-bold text-gray-900 dark:text-white text-base md:text-lg pr-8 group-hover:text-emerald-500 transition-colors">{{ faq.question }}</span>
                  <span class="w-8 h-8 rounded-full bg-gray-50 dark:bg-gray-800 shadow-sm flex items-center justify-center flex-shrink-0 border border-gray-200 dark:border-gray-700 group-hover:border-emerald-500 transition-colors">
                     <svg class="w-4 h-4 text-gray-500 dark:text-gray-400 group-hover:text-emerald-500 transition-transform duration-300" :class="{'rotate-180': openFaqIndex === index}" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"></path></svg>
                  </span>
                </button>
                <div v-show="openFaqIndex === index" class="p-5 md:p-6 pt-0 text-sm md:text-base text-gray-600 dark:text-gray-400 leading-relaxed max-w-3xl border-t border-gray-100 dark:border-white/5 bg-gray-55/30 dark:bg-black/10">
                  {{ faq.answer }}
                </div>
              </div>
            </div>
          </div>

          <!-- Image Gallery -->
          <div v-if="article.gallery_images && article.gallery_images.length > 0" class="mb-16 pt-8 border-t border-gray-100 dark:border-gray-800">
            <h3 class="text-2xl font-black text-gray-900 dark:text-white mb-8 flex items-center gap-3">
              <div class="p-2.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-xl">
                 <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
              </div>
              แกลเลอรี่ภาพเพิ่มเติม
            </h3>
            
            <!-- Modern Mosaic Grid -->
            <div class="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 auto-rows-[150px] sm:auto-rows-[200px]">
                <div 
                    v-for="(img, idx) in article.gallery_images" 
                    :key="idx" 
                    @click="openLightbox(idx)" 
                    :class="[
                        'relative group rounded-2xl overflow-hidden cursor-pointer shadow-sm border border-gray-100 dark:border-gray-800 transition-all duration-500 hover:shadow-xl hover:-translate-y-1',
                        idx === 0 && article.gallery_images.length > 2 ? 'col-span-2 row-span-2' : '',
                        idx === 1 && article.gallery_images.length > 3 ? 'col-span-2 row-span-1' : '',
                        idx === 2 && article.gallery_images.length > 3 ? 'col-span-1 row-span-1' : '',
                        idx === 3 && article.gallery_images.length > 4 ? 'col-span-1 row-span-1' : ''
                    ]"
                >
                    <img :src="img" class="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-105">
                    
                    <!-- Hover Overlay -->
                    <div class="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center backdrop-blur-[1px]">
                        <div class="bg-white/20 text-white rounded-full p-3 transform translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 backdrop-blur-md shadow-lg border border-white/30">
                            <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"></path></svg>
                        </div>
                    </div>
                </div>
            </div>
          </div>

          <!-- Tags -->
          <div v-if="article.tags && article.tags !== '[]'" class="flex flex-wrap gap-2 mb-16 pt-8 border-t border-gray-100 dark:border-gray-850">
            <span class="text-sm font-bold text-gray-455 mr-2 flex items-center gap-1">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
              แท็ก:
            </span>
            <span v-for="tag in (typeof article.tags === 'string' ? (article.tags.startsWith('[') ? JSON.parse(article.tags) : article.tags.split(',')) : article.tags)" :key="tag" class="px-3 py-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/5 text-gray-600 dark:text-gray-400 text-xs font-semibold rounded-lg shadow-sm">
              #{{ typeof tag === 'string' ? tag.trim() : tag }}
            </span>
          </div>

        </div>

        <!-- Sticky Sidebar: 4 cols -->
        <aside class="lg:col-span-4 lg:sticky lg:top-32 space-y-8 pb-10">
          
          <!-- Featured Product Widget -->
          <div v-if="article.product" class="bg-white/70 dark:bg-gray-900/30 backdrop-blur-md rounded-3xl overflow-hidden border border-gray-200 dark:border-white/5 shadow-md hover:shadow-xl transition-all duration-350 group">
             <div class="h-1 bg-gradient-to-r from-emerald-500 to-teal-400"></div>
             <div class="p-6">
               <div class="flex items-center justify-between mb-4 gap-2">
                 <span class="px-2.5 py-1.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[10px] font-black rounded-lg uppercase tracking-wider">
                   สินค้าในบทความนี้
                 </span>
                 <span v-if="article.product.category" class="text-[9px] font-bold text-gray-450 uppercase tracking-wider truncate max-w-[120px]">{{ article.product.category }}</span>
               </div>
               
               <div class="aspect-square rounded-2xl overflow-hidden bg-gray-50 dark:bg-gray-850 relative mb-4">
                 <img v-if="article.product.image_url" :src="article.product.image_url" :alt="article.product.name" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.5s]">
                 <template v-else-if="article.product.images && article.product.images[0]">
                    <img :src="article.product.images[0]" :alt="article.product.name" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.5s]">
                 </template>
                 <div v-else class="w-full h-full flex justify-center items-center text-gray-300">
                    <svg class="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                 </div>
                 <div v-if="article.product.original_price && Number(article.product.original_price) > Number(article.product.price)" class="absolute top-3 left-3 bg-red-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full shadow-md">
                   ลด {{ Math.round((1 - Number(article.product.price) / Number(article.product.original_price)) * 100) }}%
                 </div>
               </div>

               <h4 class="text-lg font-black text-gray-900 dark:text-white mb-2 leading-tight group-hover:text-emerald-500 transition-colors">{{ article.product.name }}</h4>
               <p v-if="article.product.short_description || article.product.excerpt" class="text-xs text-gray-500 dark:text-gray-400 line-clamp-3 mb-4 leading-relaxed">
                 {{ article.product.short_description || article.product.excerpt }}
               </p>
               
               <div class="flex items-center justify-between mt-4 pt-4 border-t border-gray-100 dark:border-white/5">
                 <div class="flex flex-col">
                   <span class="text-[9px] text-gray-455 uppercase font-bold">ราคาพิเศษ</span>
                   <span v-if="article.product.price && Number(article.product.price) > 0" class="text-lg font-black text-red-500">฿{{ Number(article.product.price).toLocaleString() }}</span>
                   <span v-else class="text-sm font-bold text-gray-500">สอบถามราคา</span>
                 </div>
                 <router-link :to="'/products/' + (article.product.slug || article.product.id)" class="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold rounded-xl transition-all shadow-md shadow-emerald-500/10">
                   ดูสินค้า
                 </router-link>
               </div>
             </div>
          </div>

          <!-- Authors & Sharing panel -->
          <div class="bg-white/70 dark:bg-gray-900/30 backdrop-blur-md rounded-3xl p-6 border border-gray-200 dark:border-white/5 shadow-md">
            <h4 class="text-xs font-black uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-4">ผู้แต่งบทความ</h4>
            <div class="flex items-center gap-3 mb-6">
              <div class="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold">{{ (article.author || 'A')[0] }}</div>
              <div>
                <p class="text-sm font-bold text-gray-900 dark:text-white">{{ article.author || 'Admin' }}</p>
                <p class="text-xs text-gray-400 font-medium">{{ settingsStore.storeName ? `${settingsStore.storeName} Editorial Team` : 'Editorial Team' }}</p>
              </div>
            </div>
            <div class="pt-4 border-t border-gray-100 dark:border-white/5">
              <SocialShare />
            </div>
          </div>

        </aside>

      </div>

      <!-- ═══════════════════════════════════════════════
           RECOMMENDATIONS & RELATED CONTENT TABS
           ═══════════════════════════════════════════════ -->
      <div class="border-t border-gray-150 dark:border-gray-850 pt-16 mt-16">
        <!-- Tabs Header -->
        <div class="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
          <div class="flex gap-2 p-1.5 bg-white/60 dark:bg-gray-900/50 backdrop-blur-md rounded-2xl border border-gray-200/80 dark:border-white/5 w-fit">
            <button 
              @click="activeRecommendTab = 'products'" 
              :class="activeRecommendTab === 'products' ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-sm shadow-emerald-500/15' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'"
              class="px-5 py-2.5 rounded-xl text-sm font-bold transition-all duration-300"
            >
              สินค้าที่เกี่ยวข้อง & แนะนำ
            </button>
            <button 
              @click="activeRecommendTab = 'articles'" 
              :class="activeRecommendTab === 'articles' ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-sm shadow-emerald-500/15' : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'"
              class="px-5 py-2.5 rounded-xl text-sm font-bold transition-all duration-300"
            >
              บทความอื่นๆ ที่เกี่ยวข้อง
            </button>
          </div>
          
          <!-- Slider Navigation (Only visible on desktop) -->
          <div class="hidden md:flex gap-2">
            <button @click="scrollSlider('left')" class="w-10 h-10 rounded-full border border-gray-200 dark:border-white/5 bg-white dark:bg-gray-900/40 text-gray-650 dark:text-gray-300 hover:bg-emerald-500 hover:text-white hover:border-emerald-500 flex items-center justify-center transition-all shadow-sm">
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 19l-7-7 7-7"/></svg>
            </button>
            <button @click="scrollSlider('right')" class="w-10 h-10 rounded-full border border-gray-200 dark:border-white/5 bg-white dark:bg-gray-900/40 text-gray-650 dark:text-gray-300 hover:bg-emerald-500 hover:text-white hover:border-emerald-500 flex items-center justify-center transition-all shadow-sm">
              <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7"/></svg>
            </button>
          </div>
        </div>

        <!-- Slider Content -->
        <!-- Tab 1: Products Slider -->
        <div v-show="activeRecommendTab === 'products'" class="relative">
          <div ref="productSliderRef" class="flex overflow-x-auto gap-6 pb-6 scroll-smooth snap-x snap-mandatory scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
            <!-- Product Card -->
            <router-link 
              v-for="p in [...relatedProducts, ...recommendedProducts]" 
              :key="p.id" 
              :to="'/products/' + (p.slug || p.id)" 
              class="group flex-shrink-0 w-[240px] sm:w-[280px] snap-start bg-white/60 dark:bg-gray-900/30 backdrop-blur-md rounded-3xl overflow-hidden border border-gray-200/80 dark:border-white/5 shadow-sm hover:shadow-xl hover:border-emerald-450 dark:hover:border-emerald-500/30 transition-all duration-500"
            >
              <div class="aspect-square overflow-hidden bg-gray-50 dark:bg-gray-800 relative">
                <img v-if="p.image_url" :src="p.image_url" :alt="p.name" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.5s]">
                <div v-else class="w-full h-full flex items-center justify-center text-gray-300 dark:text-gray-655">
                  <svg class="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                </div>
                <div v-if="p.original_price && p.original_price > p.price" class="absolute top-3 left-3 bg-red-500 text-white text-[10px] font-black px-2.5 py-0.5 rounded-full shadow-sm">
                  -{{ Math.round((1 - p.price / p.original_price) * 100) }}%
                </div>
              </div>
              <div class="p-5">
                <span class="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider bg-emerald-500/10 px-2.5 py-1 rounded-lg">{{ p.category }}</span>
                <h3 class="font-bold text-gray-900 dark:text-white text-base line-clamp-2 mt-3 group-hover:text-emerald-500 transition-colors leading-snug">{{ p.name }}</h3>
                <div class="mt-4 flex items-baseline gap-2 pt-3 border-t border-gray-100 dark:border-white/5">
                  <span class="text-lg font-black text-red-500">{{ formatPrice(p.price) }}</span>
                  <span v-if="p.original_price && p.original_price > p.price" class="text-xs text-gray-400 line-through font-medium">฿{{ Number(p.original_price).toLocaleString() }}</span>
                </div>
              </div>
            </router-link>
          </div>
        </div>

        <!-- Tab 2: Articles Slider -->
        <div v-show="activeRecommendTab === 'articles'" class="relative">
          <div ref="articleSliderRef" class="flex overflow-x-auto gap-6 pb-6 scroll-smooth snap-x snap-mandatory scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0">
            <!-- Article Card -->
            <router-link 
              v-for="ra in [...relatedArticles, ...recommendedArticles]" 
              :key="ra.id" 
              :to="'/blog/' + (ra.slug || ra.id)" 
              class="group flex-shrink-0 w-[280px] sm:w-[320px] snap-start bg-white/60 dark:bg-gray-900/30 backdrop-blur-md rounded-3xl overflow-hidden border border-gray-200/80 dark:border-white/5 shadow-sm hover:shadow-xl hover:border-emerald-455 dark:hover:border-emerald-500/30 transition-all duration-500"
            >
              <div class="aspect-[16/9] overflow-hidden bg-gray-100 dark:bg-gray-800 relative">
                <img v-if="ra.cover_image" :src="ra.cover_image" :alt="ra.title" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.5s]">
                <div v-else class="w-full h-full bg-gradient-to-br from-amber-50 to-orange-100 dark:from-amber-900/20 dark:to-orange-900/20 flex items-center justify-center">
                  <svg class="w-10 h-10 text-amber-350 dark:text-amber-700/50" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"></path></svg>
                </div>
                <div class="absolute top-3 left-3">
                  <span class="px-2.5 py-1 bg-white/95 dark:bg-gray-950/90 backdrop-blur-sm text-[10px] font-bold text-gray-700 dark:text-gray-300 rounded-lg shadow-sm border border-gray-100 dark:border-white/5">{{ ra.category }}</span>
                </div>
              </div>
              <div class="p-5">
                <h3 class="font-bold text-gray-900 dark:text-white text-base line-clamp-2 group-hover:text-emerald-500 transition-colors leading-snug">{{ ra.title }}</h3>
                <p v-if="ra.excerpt" class="text-xs text-gray-400 dark:text-gray-500 mt-2 line-clamp-2 leading-relaxed">{{ ra.excerpt }}</p>
                <div class="flex items-center gap-2 mt-4 pt-3 border-t border-gray-100 dark:border-white/5 text-[11px] text-gray-455">
                  <span>{{ formatDate(ra.created_at) }}</span>
                  <span class="w-1 h-1 rounded-full bg-gray-200 dark:bg-gray-800"></span>
                  <span v-if="ra.view_count" class="flex items-center gap-1">
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                    {{ ra.view_count }} วิว
                  </span>
                </div>
              </div>
            </router-link>
          </div>
        </div>
      </div>

      <!-- Back to Blog -->
      <div class="text-center mt-20">
        <router-link to="/blog" class="inline-flex items-center gap-2.5 px-6 py-3 rounded-full border border-gray-200 dark:border-white/5 bg-white dark:bg-gray-900/40 text-sm font-bold text-gray-700 dark:text-gray-300 hover:text-emerald-500 dark:hover:text-emerald-400 transition-colors shadow-sm">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/></svg>
          กลับไปหน้าบทความทั้งหมด
        </router-link>
      </div>
    </div>

    <!-- 404 -->
    <div v-else class="text-center py-24">
      <div class="w-16 h-16 bg-red-100 dark:bg-red-955/20 text-red-650 dark:text-red-400 rounded-full flex items-center justify-center mx-auto mb-6">
        <svg class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
      </div>
      <p class="text-gray-900 dark:text-white font-black text-xl mb-2">ไม่พบหน้าบทความ</p>
      <p class="text-gray-550 dark:text-gray-400">บทความที่คุณต้องการเข้าชมไม่มีอยู่จริง หรือถูกย้ายไปแล้ว</p>
      <router-link to="/blog" class="mt-6 inline-block px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-bold shadow-lg shadow-emerald-500/10">กลับหน้าบทความ</router-link>
    </div>
  </div>

  <!-- Lightbox Modal -->
  <teleport to="body">
    <div v-if="isLightboxOpen" class="fixed inset-0 z-[99999] flex items-center justify-center bg-black/95 backdrop-blur-sm" @click="closeLightbox">
    
    <!-- Close Button -->
    <button @click.stop="closeLightbox" class="absolute top-6 right-6 text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors z-[10000] focus:outline-none">
        <svg class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
    </button>

    <!-- Previous Button -->
    <button @click.stop="prevImage" class="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-white/50 hover:text-white p-3 rounded-full hover:bg-white/10 transition-all z-[10000] focus:outline-none hidden md:block border border-white/5 bg-black/20 backdrop-blur-md">
        <svg class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path></svg>
    </button>

    <!-- Next Button -->
    <button @click.stop="nextImage" class="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-white/50 hover:text-white p-3 rounded-full hover:bg-white/10 transition-all z-[10000] focus:outline-none hidden md:block border border-white/5 bg-black/20 backdrop-blur-md">
        <svg class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path></svg>
    </button>
    
    <!-- Main Image Detail -->
    <div class="relative max-w-7xl max-h-[90vh] mx-auto px-4 w-full h-full flex items-center justify-center animate-[zoomIn_0.3s_ease-out]" @click.stop>
        <img :src="article.gallery_images[currentImageIndex]" class="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl select-none" />
        
        <!-- Mobile Navigation Area Overlays -->
        <div class="absolute inset-y-0 left-0 w-1/3 z-40 md:hidden" @click="prevImage"></div>
        <div class="absolute inset-y-0 right-0 w-1/3 z-40 md:hidden" @click="nextImage"></div>
    </div>

    <!-- Image Counter -->
    <div class="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/80 font-bold text-xs px-4 py-2 bg-black/40 border border-white/5 rounded-full backdrop-blur-md pointer-events-none tracking-widest">
        {{ currentImageIndex + 1 }} / {{ article.gallery_images.length }}
    </div>
    </div>
  </teleport>
</template>

<style scoped>
/* ══════════════════════════════════════════════════
   CKEditor Content Styling - BlogDetail
   ══════════════════════════════════════════════════ */

/* ── Headings ── */
.ck-content :deep(h2) {
  font-size: 1.5rem;
  font-weight: 800;
  color: #1f2937;
  margin-top: 1.75rem;
  margin-bottom: 0.75rem;
  line-height: 1.35;
  letter-spacing: -0.015em;
  border-left: 4px solid #10b981;
  padding-left: 0.75rem;
}

.ck-content :deep(h3) {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1f2937;
  margin-top: 1.5rem;
  margin-bottom: 0.625rem;
  line-height: 1.35;
}

.ck-content :deep(h4) {
  font-size: 1.1rem;
  font-weight: 700;
  color: #374151;
  margin-top: 1.25rem;
  margin-bottom: 0.5rem;
  line-height: 1.4;
}

:global(.dark) .ck-content :deep(h2) {
  color: #f3f4f6;
  border-left-color: #059669;
}
:global(.dark) .ck-content :deep(h3) { color: #f3f4f6; }
:global(.dark) .ck-content :deep(h4) { color: #e5e7eb; }

/* ── Paragraphs ── */
.ck-content :deep(p) {
  color: #374151;
  line-height: 1.7;
  margin-bottom: 0.875rem;
  font-weight: 400;
}

:global(.dark) .ck-content :deep(p) {
  color: #d1d5db;
}

/* ── Strong / Bold ── */
.ck-content :deep(strong) {
  color: #111827;
  font-weight: 700;
}

:global(.dark) .ck-content :deep(strong) {
  color: #f9fafb;
}

/* ── Lists ── */
.ck-content :deep(ul),
.ck-content :deep(ol) {
  padding-left: 1.375rem;
  margin-top: 0.5rem;
  margin-bottom: 1rem;
}

.ck-content :deep(ul) {
  list-style-type: disc;
}

.ck-content :deep(ol) {
  list-style-type: decimal;
}

.ck-content :deep(li) {
  margin-bottom: 0.4375rem;
  line-height: 1.65;
  color: #374151;
  padding-left: 0.125rem;
}

:global(.dark) .ck-content :deep(li) {
  color: #d1d5db;
}

.ck-content :deep(li::marker) {
  color: #10b981;
  font-weight: 700;
}

/* ── Links ── */
.ck-content :deep(a) {
  color: #059669;
  text-decoration: underline;
  text-underline-offset: 3px;
  text-decoration-color: #a7f3d0;
  transition: all 0.2s;
}

.ck-content :deep(a:hover) {
  color: #047857;
  text-decoration-color: #059669;
}

:global(.dark) .ck-content :deep(a) {
  color: #34d399;
  text-decoration-color: #065f46;
}

/* ── Blockquote ── */
.ck-content :deep(blockquote) {
  border-left: 4px solid #10b981;
  background-color: #f9fafb;
  padding: 1.25rem 1.5rem;
  margin: 2rem 0;
  border-radius: 0 12px 12px 0;
  font-style: italic;
  color: #374151;
}

:global(.dark) .ck-content :deep(blockquote) {
  background-color: #1f2937;
  border-left-color: #059669;
  color: #d1d5db;
}

/* ── Media (Video/YouTube) ── */
.ck-content :deep(.yt-embed-wrapper) {
  max-width: 100%;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15), 0 1px 4px rgba(0, 0, 0, 0.1);

  transition: box-shadow 0.3s ease, transform 0.3s ease;
}
.ck-content :deep(.yt-embed-wrapper:hover) {
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25), 0 2px 8px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}
.ck-content :deep(.yt-embed-inner) {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 */
  height: 0;
  overflow: hidden;
}
.ck-content :deep(.yt-embed-inner iframe) {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

.ck-content :deep(figure.media) {
  margin: 2rem auto;
  width: 100%;
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15), 0 1px 4px rgba(0, 0, 0, 0.1);
  padding-bottom: 56.25%; /* 16:9 Aspect Ratio */
  height: 0;

}
:global(.dark) .ck-content :deep(figure.media) {

}
.ck-content :deep(figure.media iframe) {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

/* ── Images ── */
.ck-content :deep(figure.image) {
  margin: 2rem auto;
  text-align: center;
}

.ck-content :deep(figure.image img) {
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  max-width: 100%;
  height: auto;
}

.ck-content :deep(figure.image figcaption) {
  font-size: 0.85rem;
  color: #6b7280;
  margin-top: 0.75rem;
  font-style: italic;
}

:global(.dark) .ck-content :deep(figure.image figcaption) {
  color: #9ca3af;
}

/* ── Horizontal Rule ── */
.ck-content :deep(hr) {
  border: none;
  height: 2px;
  background: linear-gradient(90deg, transparent, #d1fae5, transparent);
  margin: 2.5rem 0;
}

:global(.dark) .ck-content :deep(hr) {
  background: linear-gradient(90deg, transparent, #064e3b, transparent);
}

/* ══════════════════════════════════════════════════
   Tables
   ══════════════════════════════════════════════════ */

.ck-content :deep(figure.table) {
  display: flex;
  justify-content: center;
  margin: 2rem auto;
  width: 100%;
}

.ck-content :deep(table) {
  width: 100% !important;
  table-layout: auto;
  border-collapse: separate !important;
  border-spacing: 0 !important;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04);
  border: 1px solid #d1fae5 !important;
  margin: 2rem auto;
  font-size: 0.95rem;
}

.ck-content :deep(table thead) {
  background: linear-gradient(135deg, #059669, #10b981);
}

.ck-content :deep(table thead th) {
  color: white;
  font-weight: 700;
  padding: 14px 20px;
  text-align: left;
  letter-spacing: 0.02em;
  border-bottom: 2px solid #047857;
  white-space: nowrap;
}

.ck-content :deep(table thead th:first-child) {
  border-top-left-radius: 16px;
}

.ck-content :deep(table thead th:last-child) {
  border-top-right-radius: 16px;
}

.ck-content :deep(table tbody tr) {
  transition: background-color 0.2s ease;
}

.ck-content :deep(table tbody tr:nth-child(even)) {
  background-color: #f0fdf4;
}

.ck-content :deep(table tbody tr:hover) {
  background-color: #dcfce7;
}

.ck-content :deep(table tbody td) {
  padding: 12px 20px;
  border-bottom: 1px solid #ecfdf5;
  color: #374151;
  line-height: 1.6;
  vertical-align: top;
  min-width: 120px;
}

.ck-content :deep(table tbody td:first-child) {
  font-weight: 600;
  color: #065f46;
  min-width: 160px;
}

.ck-content :deep(table tbody td:last-child) {
  /* removed forced width: 100% to prevent squashing other columns */
}

.ck-content :deep(table tbody tr:last-child td) {
  border-bottom: none;
}

.ck-content :deep(table tbody tr:last-child td:first-child) {
  border-bottom-left-radius: 16px;
}

.ck-content :deep(table tbody tr:last-child td:last-child) {
  border-bottom-right-radius: 16px;
}

/* ── Dark Mode Tables ── */
:global(.dark) .ck-content :deep(table) {
  border-color: #064e3b;
  box-shadow: 0 1px 3px rgba(0,0,0,0.3), 0 4px 12px rgba(0,0,0,0.2);
}

:global(.dark) .ck-content :deep(table thead) {
  background: linear-gradient(135deg, #047857, #059669);
}

:global(.dark) .ck-content :deep(table thead th) {
  border-bottom-color: #065f46;
}

:global(.dark) .ck-content :deep(table tbody tr:nth-child(even)) {
  background-color: #022c22;
}

:global(.dark) .ck-content :deep(table tbody tr:hover) {
  background-color: #064e3b;
}

:global(.dark) .ck-content :deep(table tbody td) {
  border-bottom-color: #1e293b;
  color: #cbd5e1;
}

:global(.dark) .ck-content :deep(table tbody td:first-child) {
  color: #a7f3d0;
}

/* ── Responsive ── */
@media (max-width: 640px) {
  .ck-content :deep(table) {
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}
</style>

